import {
  businessDay,
  fmt,
  fmtDate,
  hasValue,
  parseKip,
  presetRange,
  todayKey,
  type ExpenseJournal,
} from '@k2pos/core';
import { useCallback, useMemo, useState } from 'react';
import { AddValueSheet } from '@/components/ui/AddValueSheet';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { UnitChip } from '@/components/ui/Chip';
import { ConfirmSheet } from '@/components/ui/ConfirmSheet';
import { ErrorBanner } from '@/components/ui/ErrorBanner';
import { Field } from '@/components/ui/Field';
import { Mono, Text } from '@/components/ui/Text';
import { IconPlus } from '@/components/icons';
import { cx } from '@/lib/cx';
import { useBackHandling, useGoBack } from '@/hooks/useNav';
import { useMoneyAction } from '@/hooks/useMoneyAction';
import { usePos, type NewExpense } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Expense.module.css';

/** Round amounts a shop reaches for most often. */
const PRESETS = [50_000, 100_000, 200_000, 500_000];

/** ລົງລາຍຈ່າຍ — money out that is not goods. */
export function Expense() {
  const goBack = useGoBack('/more');
  const categories = usePos((s) => s.expenseCategories);
  const journal = usePos((s) => s.journal);
  const addExpense = usePos((s) => s.addExpense);
  const reverseExpense = usePos((s) => s.reverseExpense);
  const addExpenseCategory = usePos((s) => s.addExpenseCategory);

  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState(categories[0] ?? 'ອື່ນໆ');
  const [note, setNote] = useState('');
  const [cash, setCash] = useState(true);
  const [day, setDay] = useState(() => businessDay(new Date()));
  const [adding, setAdding] = useState(false);
  /** The expense the shopkeeper tapped ຍົກເລີກ on, held until they confirm. */
  const [toUndo, setToUndo] = useState<ExpenseJournal | null>(null);

  const value = parseKip(amount);
  const valid = value !== null && value > 0;

  const commit = useCallback((input: NewExpense) => addExpense(input), [addExpense]);
  const { run, busy, error, dismiss } = useMoneyAction(commit, () => {
    setAmount('');
    setNote('');
  });

  const undo = useCallback((id: string) => reverseExpense(id), [reverseExpense]);
  const {
    run: runUndo,
    busy: undoing,
    error: undoError,
    dismiss: dismissUndo,
  } = useMoneyAction(undo, () => setToUndo(null));

  useBackHandling(false, adding || toUndo ? 1 : 0, () => {
    setAdding(false);
    setToUndo(null);
  });

  const today = useMemo(() => {
    const range = presetRange('today', todayKey());
    return journal.filter(
      (e): e is ExpenseJournal =>
        e.kind === 'expense' && e.day >= range.from && e.day <= range.to,
    );
  }, [journal]);

  const total = today.reduce((sum, e) => sum + e.amount, 0);
  const reversed = new Set(today.map((e) => e.reversalOf).filter(Boolean));

  function save() {
    if (!valid) return;
    run({
      category,
      amount: value,
      note,
      cash,
      at: new Date(`${day}T${new Date().toTimeString().slice(0, 8)}`).toISOString(),
    });
  }

  return (
    <>
      <AppBar
        title="ລົງລາຍຈ່າຍ"
        onBack={goBack}
        subtitle={
          <Text size={11} color={colors.onDarkSoft}>
            ມື້ນີ້{' '}
            <Mono weight={700} size={13} color={colors.sky}>
              {fmt(total)}
            </Mono>{' '}
            ₭
          </Text>
        }
      />

      <div className={cx('vscroll', styles.body)}>
        <ErrorBanner message={error} onDismiss={dismiss} />
        <ErrorBanner message={undoError} onDismiss={dismissUndo} />

        <Labelled label="ຈຳນວນ (₭)">
          <Field value={amount} onChange={setAmount} kind="kip" placeholder="150000" label="ຈຳນວນເງິນ" />
          <div className={cx('hscroll', styles.chips)}>
            {PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setAmount(String(v))}
                className={styles.preset}
              >
                <Mono weight={700} size={11.5} color={colors.navy}>
                  {fmt(v)}
                </Mono>
              </button>
            ))}
          </div>
        </Labelled>

        <Labelled label="ໝວດ">
          <div className={styles.units} role="radiogroup" aria-label="ໝວດລາຍຈ່າຍ">
            {categories.map((c) => (
              <UnitChip key={c} unit={c} selected={c === category} onPress={() => setCategory(c)} />
            ))}
            <button type="button" onClick={() => setAdding(true)} className={styles.addChip}>
              <IconPlus size={13} />
            </button>
          </div>
        </Labelled>

        <Labelled label="ໝາຍເຫດ · ບໍ່ບັງຄັບ">
          <Field value={note} onChange={setNote} label="ໝາຍເຫດ" placeholder="ເຊັ່ນ ຄ່າລົດສົ່ງຊີມັງ" />
        </Labelled>

        <Labelled label="ວັນທີ">
          <div className={styles.dateRow}>
            <input
              type="date"
              value={day}
              max={businessDay(new Date())}
              onChange={(e) => setDay(e.target.value || businessDay(new Date()))}
              aria-label="ວັນທີລາຍຈ່າຍ"
              className={styles.dateInput}
            />
            {/* The native picker renders in the device locale, so the date is
                echoed through fmtDate, which is pinned to en-US. */}
            <Mono size={11.5} color={colors.textMuted}>
              {fmtDate(new Date(`${day}T00:00:00`))}
            </Mono>
          </div>
        </Labelled>

        <Labelled label="ຈ່າຍດ້ວຍ">
          <div className={styles.toggles}>
            <button
              type="button"
              role="switch"
              aria-checked={cash}
              onClick={() => setCash(true)}
              className={cx(styles.toggle, cash && styles.toggleOn)}
            >
              <Text weight={700} size={12} color={cash ? colors.surfaceAlt : colors.textMuted}>
                ເງິນສົດ
              </Text>
            </button>
            <button
              type="button"
              role="switch"
              aria-checked={!cash}
              onClick={() => setCash(false)}
              className={cx(styles.toggle, !cash && styles.toggleOn)}
            >
              <Text weight={700} size={12} color={!cash ? colors.surfaceAlt : colors.textMuted}>
                ໂອນ
              </Text>
            </button>
          </div>
          {/* This flag is load-bearing, not decoration: the report's drawer
              figure is what the owner checks against the actual till at night,
              and it can only balance if cash out is known. */}
          <Text size={10} color={colors.textFaint}>
            ເງິນສົດຈະຫັກອອກຈາກເງິນໃນລິ້ນຊັກ
          </Text>
        </Labelled>

        <Button
          label={busy ? 'ກຳລັງບັນທຶກ...' : 'ບັນທຶກລາຍຈ່າຍ'}
          disabled={!valid || busy}
          onPress={save}
        />

        <Text weight={800} size={11} color={colors.textMuted} className={styles.label}>
          ລາຍຈ່າຍມື້ນີ້
        </Text>

        {today.map((e) => {
          const isReversal = e.amount < 0;
          const wasReversed = reversed.has(e.id);
          return (
            <Card
              key={e.id}
              className={cx(styles.row, (isReversal || wasReversed) && styles.rowVoid)}
            >
              <div className="grow col">
                <Text weight={700} size={12.5} lines={1}>
                  {e.category}
                  {isReversal ? ' · ຍົກເລີກ' : ''}
                </Text>
                <Mono size={10.5} color={colors.textFaint} lines={1}>
                  {e.cash ? 'ເງິນສົດ' : 'ໂອນ'}
                  {e.note ? ` · ${e.note}` : ''}
                </Mono>
              </div>
              <Mono weight={700} size={13} color={isReversal ? colors.textFaint : colors.text}>
                {fmt(e.amount)}
              </Mono>
              {!isReversal && !wasReversed ? (
                <button
                  type="button"
                  disabled={undoing}
                  onClick={() => setToUndo(e)}
                  className={styles.undo}
                >
                  <Text weight={700} size={11} color={colors.skyDeep}>
                    ຍົກເລີກ
                  </Text>
                </button>
              ) : null}
            </Card>
          );
        })}

        {today.length === 0 ? (
          <Text size={12} color={colors.textMuted} align="center" className={styles.empty}>
            ຍັງບໍ່ມີລາຍຈ່າຍມື້ນີ້
          </Text>
        ) : null}
      </div>

      <AddValueSheet
        open={adding}
        title="ເພີ່ມໝວດລາຍຈ່າຍ"
        placeholder="ເຊັ່ນ ຄ່າສ້ອມແປງ"
        validate={(v) => (hasValue(categories, v) ? 'ມີຢູ່ແລ້ວ' : null)}
        onAdd={(v) => {
          addExpenseCategory(v);
          setCategory(v);
          setAdding(false);
        }}
        onClose={() => setAdding(false)}
      />

      <ConfirmSheet
        open={toUndo !== null}
        title="ຍົກເລີກລາຍຈ່າຍນີ້ບໍ?"
        subject={
          toUndo ? (
            <>
              <Text weight={800} size={13.5} align="left">
                {toUndo.category}
              </Text>
              <Text size={11.5} color={colors.textMuted} align="left">
                <Mono weight={700} size={13}>
                  {fmt(toUndo.amount)}
                </Mono>{' '}
                ₭ · {toUndo.cash ? 'ເງິນສົດ' : 'ໂອນ'}
                {toUndo.note ? ` · ${toUndo.note}` : ''}
              </Text>
            </>
          ) : null
        }
        // The reversing entry is the honest record and the shopkeeper should
        // know it will be there; a line that just vanished would leave them
        // hunting for it in the report tonight.
        message="ຈະລົງລາຍການກົງກັນຂ້າມໄວ້ ລາຍການເດີມຍັງເຫັນຢູ່ ແຕ່ຫັກລົບກັນເປັນສູນ"
        confirmLabel="ຍົກເລີກລາຍຈ່າຍ"
        busy={undoing}
        onConfirm={() => toUndo && runUndo(toUndo.id)}
        onClose={() => setToUndo(null)}
      />
    </>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className={styles.field}>
      <Text weight={700} size={11} color={colors.textMuted}>
        {label}
      </Text>
      {children}
    </div>
  );
}
